import { type ReactNode } from 'react'
import {
  type ViewHeadingProps,
  ViewHeading,
  type ViewHeadingSize,
} from '@/components/ViewHeading'
import {
  Dropdown,
  DropdownTitle,
  DropdownItem,
  DropdownDivider,
  DropdownScroll,
} from '@/components/Dropdown'
import { RawButton } from '@/components/Button'
import { SearchField } from '@/components/SearchField'
import styles from './WorkspaceSelectorViewHeading.module.css'

export interface WorkspaceSelectorOption {
  id: string
  label: ReactNode
  isSelected: boolean
}

export interface WorkspaceSelectorViewHeadingProps extends Omit<
  ViewHeadingProps,
  'isDropdown' | 'secondaryText'
> {
  options: WorkspaceSelectorOption[]
  isAllSelected: boolean
  secondaryText: ReactNode
  onSelect: (id: string) => void
  onSelectAll: () => void
  dropdownTitle?: string
  allOptionDropdownItem?: ReactNode
  allOptionText: string | null
  alwaysUseDropdown?: boolean
  /**
   * Makes the dropdown searchable and controlled. Leave `undefined` for an
   * uncontrolled dropdown without search.
   */
  search?: {
    text: string
    onTextChange: (text: string) => void
    isOpen: boolean
    onOpen: () => void
    onClose: () => void
  }
  viewHeadingSize?: ViewHeadingSize
  showClearButton?: boolean
}

export function WorkspaceSelectorViewHeading({
  options,
  isAllSelected,
  secondaryText,
  onSelect,
  onSelectAll,
  allOptionDropdownItem,
  search,
  allOptionText,
  dropdownTitle = 'Workspaces',
  alwaysUseDropdown = false,
  viewHeadingSize = 'normal',
  showClearButton = false,
  ...rest
}: WorkspaceSelectorViewHeadingProps) {
  const shouldUseDropdown = alwaysUseDropdown || options.length > 1
  const heading = (
    <ViewHeading
      {...rest}
      isDropdown={shouldUseDropdown}
      secondaryText={secondaryText}
      viewHeadingSize={viewHeadingSize}
    />
  )
  return shouldUseDropdown ? (
    <Dropdown
      button={heading}
      autoDismiss={!search}
      isOpen={search?.isOpen}
      position="bottom-start"
      testId="workspace-selector-dropdown"
      onButtonPress={search?.onOpen}
      onRequestClose={search?.onClose}
    >
      {!allOptionDropdownItem && allOptionText && (
        <>
          <DropdownItem
            active={isAllSelected}
            checkmark={isAllSelected}
            testId="all-workspaces"
            onPress={onSelectAll}
          >
            {allOptionText}
          </DropdownItem>
          <DropdownDivider />
        </>
      )}

      {showClearButton && search?.text !== '' ? (
        <div className={styles.actionHeadingContainer}>
          <DropdownTitle>{dropdownTitle}</DropdownTitle>
          <RawButton
            className={styles.clearTextButton}
            onPress={() => search?.onTextChange('')}
          >
            Clear
          </RawButton>
        </div>
      ) : (
        <DropdownTitle>{dropdownTitle}</DropdownTitle>
      )}

      {search && (
        <WorkspaceSearch
          text={search.text}
          onTextChange={search.onTextChange}
        />
      )}
      <DropdownScroll>
        {allOptionText && allOptionDropdownItem && (
          <DropdownItem
            active={isAllSelected}
            checkmark={isAllSelected}
            testId="all-workspaces"
            onPress={() => {
              search?.onClose()
              onSelectAll()
            }}
          >
            {allOptionDropdownItem}
          </DropdownItem>
        )}
        {options.map((option) => (
          <DropdownItem
            key={option.id}
            active={option.isSelected}
            checkmark={option.isSelected}
            onPress={() => {
              search?.onClose()
              onSelect(option.id)
            }}
            testId={`workspace-${option.id}-dropdown-item`}
          >
            {option.label}
          </DropdownItem>
        ))}
      </DropdownScroll>
    </Dropdown>
  ) : (
    heading
  )
}

interface WorkspaceSearchProps {
  text: string
  onTextChange: (text: string) => void
}

function WorkspaceSearch({ text, onTextChange }: WorkspaceSearchProps) {
  return (
    <div className={styles.search}>
      <SearchField
        appearance="outlined"
        stretch
        value={text}
        onChange={onTextChange}
        onClick={(evt) => evt.stopPropagation()}
        testId="workspace-search-input"
      />
    </div>
  )
}
